import React, { useEffect, useState } from "react";
import { Check, X, BellRing, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent } from "../ui/dialog";
import { startRinging, stopRinging, unlockAudio } from "../../lib/chime";

// A loud, blocking "ring" popup for new orders (restaurant) and new ride requests (driver).
// requireRejectReason → the reject action asks for a reason before confirming.
export function RingModal({ open, title, subtitle, accent = "brand", onAccept, onReject, onSnooze, acceptLabel = "Accept", rejectLabel = "Reject", requireRejectReason = false, rejectPresets = [], children }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) { unlockAudio(); startRinging(); } else { stopRinging(); setRejecting(false); setReason(""); }
    return () => stopRinging();
  }, [open]);

  const accept = async () => { setBusy(true); stopRinging(); try { await onAccept(); } finally { setBusy(false); } };
  const rejectWith = async (r) => { setBusy(true); stopRinging(); try { await onReject((r || "").trim()); } finally { setBusy(false); } };
  const reject = async () => {
    if (requireRejectReason && !rejecting) { setRejecting(true); return; }
    rejectWith(reason);
  };
  const snooze = async () => { if (!onSnooze) return; stopRinging(); await onSnooze(); };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-md border-0 p-0 overflow-hidden [&>button:last-child]:hidden" data-testid="ring-modal" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <div className={`flex items-center gap-3 px-5 py-4 text-white ${accent === "graphite" ? "bg-graphite" : "bg-brand"}`}>
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 kt-pulse-ring"><BellRing className="h-5 w-5" /></span>
          <div className="min-w-0"><p className="text-base font-bold leading-tight" data-testid="ring-title">{title}</p>{subtitle && <p className="text-xs text-white/80">{subtitle}</p>}</div>
        </div>
        <div className="space-y-4 px-5 pb-5 pt-4">
          <div data-testid="ring-body">{children}</div>
          {rejecting ? (
            <div className="space-y-2">
              {rejectPresets.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="reject-presets">
                  {rejectPresets.map((p) => (
                    <button key={p} type="button" onClick={() => rejectWith(p)} disabled={busy} className="rounded-full border border-surface-300 px-3 py-1.5 text-xs font-medium text-ink-soft transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-700" data-testid={`reject-preset-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{p}</button>
                  ))}
                </div>
              )}
              <Textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Or type a custom reason…" data-testid="ring-reject-reason" />
              <div className="flex gap-2">
                <Button onClick={reject} disabled={busy} variant="outline" className="flex-1 rounded-full border-red-300 text-red-700 hover:bg-red-50" data-testid="ring-reject-confirm">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm reject</Button>
                <Button onClick={() => setRejecting(false)} variant="ghost" className="rounded-full">Back</Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button onClick={accept} disabled={busy} className={`flex-1 rounded-full text-white ${accent === "graphite" ? "bg-graphite hover:bg-graphite-600" : "bg-brand hover:bg-brand-600"}`} data-testid="ring-accept">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />} {acceptLabel}</Button>
              <Button onClick={reject} disabled={busy} variant="outline" className="rounded-full" data-testid="ring-reject"><X className="mr-2 h-4 w-4" /> {rejectLabel}</Button>
            </div>
          )}
          {onSnooze && !rejecting && (
            <button type="button" onClick={snooze} disabled={busy} className="w-full text-xs font-semibold text-ink-muted hover:text-ink" data-testid="ring-snooze">Snooze 1 min</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
